import React from 'react';
import { XMarkIcon, DocumentTextIcon, ArrowDownTrayIcon, BuildingOffice2Icon, CalendarIcon, CurrencyDollarIcon, HashtagIcon, TruckIcon, CubeIcon, EyeIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import type { XmlCte } from '../../types/cte';
import { useBaixarXmlCte } from '../../hooks/useCte';
import { money, formatCnpj, formatTipoFrete, formatDateTime } from '../../utils/format';
import { btnPrimario, btnSecundario } from '../ui/iris';

interface CteModalProps {
  cte: XmlCte | null;
  isOpen: boolean;
  onClose: () => void;
}

const CteModal: React.FC<CteModalProps> = ({
  cte,
  isOpen,
  onClose
}) => {
  const baixarXmlMutation = useBaixarXmlCte();


  const handleBaixarXml = () => {
    if (cte) {
      baixarXmlMutation.mutate(cte.id);
    }
  };

  if (!isOpen || !cte) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-[var(--scrim)] backdrop-blur-[2px] transition-opacity" onClick={onClose} />

        <div
          role="dialog"
          aria-modal="true"
          aria-label="Detalhes do CT-e"
          className="relative transform overflow-hidden rounded-iris-lg bg-surface border border-line-strong text-left shadow-iris-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-line">
            <div className="flex items-center gap-3">
              <div className="bg-accent p-2 rounded-iris">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-tx">
                  Detalhes do CTe - {cte.numero || 'S/N'}
                </h2>
                <p className="text-sm text-tx-2">
                  Informações completas do conhecimento de transporte
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-2 rounded-iris transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-tx-3" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Informações do Documento */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-tx flex items-center gap-2">
                  <HashtagIcon className="h-5 w-5 text-tx-3" />
                  Informações do Documento
                </h3>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <HashtagIcon className="h-5 w-5 text-tx-3 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-tx-2">Número do CTe</p>
                      <p className="font-semibold text-tx">{cte.numero || 'Não informado'}</p>
                    </div>
                  </div>

                  {cte.serie && (
                    <div className="flex items-center gap-3">
                      <EyeIcon className="h-5 w-5 text-tx-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-tx-2">Série</p>
                        <p className="font-semibold text-tx">{cte.serie}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <EyeIcon className="h-5 w-5 text-tx-3 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-tx-2">NSU</p>
                      <p className="font-mono text-sm text-tx">{cte.nsu}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <CalendarIcon className="h-5 w-5 text-tx-3 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-tx-2">Data de Emissão</p>
                      <p className="font-semibold text-tx">{formatDateTime(cte.dataEmissao)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <CalendarIcon className="h-5 w-5 text-tx-3 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-tx-2">Data de Processamento</p>
                      <p className="font-semibold text-tx">{formatDateTime(cte.dataProcessamento)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Informações Financeiras */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-tx flex items-center gap-2">
                  <CurrencyDollarIcon className="h-5 w-5 text-tx-3" />
                  Informações Financeiras
                </h3>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CurrencyDollarIcon className="h-5 w-5 text-tx-3 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-tx-2">Valor Total</p>
                      <p className="text-xl font-bold text-ok">{money(cte.valorTotal)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <CurrencyDollarIcon className="h-5 w-5 text-tx-3 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-tx-2">Valor do Frete</p>
                      <p className="font-semibold text-tx">{money(cte.valorFrete)}</p>
                    </div>
                  </div>

                  {cte.tipoPagamento && (
                    <div className="flex items-center gap-3">
                      <CreditCardIcon className="h-5 w-5 text-tx-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-tx-2">Modalidade do Frete</p>
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold ${formatTipoFrete(cte.tipoPagamento).color}`}>
                            {formatTipoFrete(cte.tipoPagamento).label}
                          </span>
                          <span className="text-xs bg-surface-2 px-2 py-1 rounded-full text-tx-2">
                            {cte.tipoPagamento}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {cte.tipoServico && (
                    <div className="flex items-center gap-3">
                      <EyeIcon className="h-5 w-5 text-tx-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-tx-2">Tipo de Serviço</p>
                        <p className="font-semibold text-tx">{cte.tipoServico}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Informações das Empresas */}
            <div className="mt-8 space-y-6">
              <h3 className="text-lg font-semibold text-tx flex items-center gap-2">
                <BuildingOffice2Icon className="h-5 w-5 text-tx-3" />
                Informações das Empresas
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Emitente */}
                <div className="bg-surface-2 p-4 rounded-iris">
                  <h4 className="font-semibold text-tx mb-3 flex items-center gap-2">
                    <BuildingOffice2Icon className="h-4 w-4 text-tx-3" />
                    Emitente
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-tx-2">Nome</p>
                      <p className="font-medium text-tx">{cte.emitenteNome || 'Não informado'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-tx-2">CNPJ</p>
                      <p className="font-mono text-sm text-tx">{formatCnpj(cte.emitenteCnpj)}</p>
                    </div>
                  </div>
                </div>

                {/* Remetente */}
                <div className="bg-surface-2 p-4 rounded-iris">
                  <h4 className="font-semibold text-tx mb-3 flex items-center gap-2">
                    <TruckIcon className="h-4 w-4 text-tx-3" />
                    Remetente
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-tx-2">Nome</p>
                      <p className="font-medium text-tx">{cte.remetenteNome || 'Não informado'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-tx-2">CNPJ</p>
                      <p className="font-mono text-sm text-tx">{formatCnpj(cte.remetenteCnpj)}</p>
                    </div>
                  </div>
                </div>

                {/* Destinatário */}
                <div className="bg-surface-2 p-4 rounded-iris">
                  <h4 className="font-semibold text-tx mb-3 flex items-center gap-2">
                    <CubeIcon className="h-4 w-4 text-tx-3" />
                    Destinatário
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-tx-2">Nome</p>
                      <p className="font-medium text-tx">{cte.destinatarioNome || 'Não informado'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-tx-2">CNPJ</p>
                      <p className="font-mono text-sm text-tx">{formatCnpj(cte.destinatarioCnpj)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chave de Acesso */}
            {cte.chaveAcesso && (
              <div className="mt-6 bg-accent-soft p-4 rounded-iris">
                <h4 className="font-semibold text-tx mb-2">Chave de Acesso</h4>
                <p className="font-mono text-sm text-tx-2 break-all">{cte.chaveAcesso}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-surface-2 px-6 py-4 flex justify-end gap-3 border-t border-line">
            <button
              onClick={onClose}
              className={btnSecundario}
            >
              Fechar
            </button>
            <button
              onClick={handleBaixarXml}
              disabled={baixarXmlMutation.isPending}
              className={btnPrimario}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {baixarXmlMutation.isPending ? 'Baixando...' : 'Baixar XML'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CteModal;